import {Injectable} from '@angular/core';
import {HackUtils} from '../utils/Utils';
import {AuthorizedApiService} from './authorized-api.service';
import {ChargeItem, Order} from '../models/Order';
import {EMPTY, firstValueFrom, mergeMap, Observable, of, throwError} from 'rxjs';
import {BamboraSession, BamboraTokenizedAuth, SwishLink, VippsLink} from '../models/PaymentModels';
import {map} from 'rxjs/operators';
import {OrderService} from './order.service';
import {AuthService} from './auth.service';
import {User} from 'firebase/auth';
import {AppStateService} from './app-state.service';

// Where Swish or Vipps sends the guest back: the paystatus page, with sec + uid so it can sign the guest in again.
// Built from the path alone. The current query must not ride along: since the app keeps the scanned ?table= in every
// URL, gluing it onto the href put "?table=CODE" inside the uid and every return from Swish failed with
// "Invalid security token" (2026-10-27). Pages the app does not pay from return their href unchanged.
export function payStatusHref(currentHref: string, uid: string, plKey?: string): string {
  const url = new URL(currentHref);
  const path = url.pathname;
  let returnPath: string;
  let origin: string;
  if (path.includes("/livesplit")) {
    returnPath = path.replace("/livesplit", "/paystatus/RECEIPT_KEY");
    origin = "&from=livesplit";
  } else if (path.includes("/cart")) {
    returnPath = path.replace("/cart", "/paystatus/RECEIPT_KEY");
    origin = "&from=cart";
  } else if (path.includes("/pl/")) {
    // Everything after /pl/ goes. The link key rides along so a declined payment can go back to the payment link
    // page (the in-app history is gone after an app switch).
    returnPath = path.split("/pl/")[0] + "/paystatus/RECEIPT_KEY";
    origin = plKey ? `&pl=${encodeURIComponent(plKey)}` : "";
  } else {
    return currentHref;
  }
  // `from` names the page the payment started on, so a declined payment lands on that page's failure view.
  return `${url.origin}${returnPath}?sec=_SECURITY_&uid=${encodeURIComponent(uid)}${origin}`;
}

/**
 * Where a declined or failed app-switch payment goes: the failure view of the page it started on. A payment link
 * goes back to the link (the table bill may be empty), a bill payment to the bill, anything else to the cart.
 */
export function paymentFailedPath(from: string | null | undefined, plKey: string | null | undefined): string {
  if (plKey) {
    return `livesplit/paymentfailed?pl=${encodeURIComponent(plKey)}`;
  }
  return from === "livesplit" ? "livesplit/paymentfailed" : "cart/paymentfailed";
}

export interface PaymentInputData {
  tipParam: string;
  method: string;

  receipt_key?: string;
  itemRefs?: any[];
  plKey?: string;
  payableRefs?: string;
  chargeItems?: ChargeItem[];
  subType?: string;
  declineRoute?: string;
}

export interface PaymentOutputData {
  method: string
  checkPayStatusReceiptKey?: string;
  methodResponse: any;
  navUrl?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PaymentService {

  constructor(private api: AuthorizedApiService, private orderService: OrderService, private auth: AuthService, private appState: AppStateService) {}

  private payBambora(pid: PaymentInputData): Observable<BamboraSession> {
    return this.api.createBamboraSession(pid.tipParam, pid.receipt_key, pid.itemRefs, pid.plKey, pid.payableRefs, pid.chargeItems, pid.subType, pid.declineRoute).pipe(
      mergeMap(session => this.openBamboraCheckout(session))
    );
  }

  private pay_bambora_saved_card(pid: PaymentInputData) {
    return this.api.chargeSavedCard(pid.tipParam, pid.receipt_key, pid.itemRefs, pid.declineRoute, HackUtils.getBrowserInfo(), pid.plKey, pid.payableRefs, pid.chargeItems).pipe(
      mergeMap(bamTok => {
        if (bamTok.method !== "noredirect") {
          this.redirectBamboraSCA(bamTok);
        }
        return of(bamTok);
      })
    );
  }

  private redirectBamboraSCA(bamTok: BamboraTokenizedAuth) {
    if (bamTok.method === "post") {
      console.log("Redirect with POST");
      let inputs = "";
      for (const param of bamTok.parameters) {
        inputs += `<input name="${param.name}" type="hidden" value="${param.value}" />`;
      }
      const html = `<form id="innerFormPostLS" method="post" action="${bamTok.redirect_url}">${inputs}</form>`;
      document.getElementById("formPostDivLS")!.innerHTML = html;
      console.log(html);
      const innerFormPost = document.getElementById("innerFormPostLS");
      console.log(innerFormPost);
      if (innerFormPost) {
        // @ts-ignore
        innerFormPost.submit();
      }
    } else if (bamTok.method === "redirect") {
      console.log("Redirect to", bamTok.redirect_url);
      window.location.replace(bamTok.redirect_url);
    }
  }

  // The paystatus page as the return address for an app switch (Swish, Vipps). The server fills in RECEIPT_KEY
  // and _SECURITY_; sec + uid let the page sign the guest in again if the switch lands in another browser/tab.
  private getPayStatusHref(user: User, plKey?: string): string {
    const href = payStatusHref(window.location.href, user.uid, plKey);
    console.log("payStatusHref", href);
    return href;
  }

  private openSwishApp(sl: SwishLink): string {
    // Patch link with receipt key
    const link = sl.link.replace("RECEIPT_KEY", sl.receipt_key);
    console.log("redirect to: " + link);
    window.location.replace(link);
    return link;
  }

  private paySwish(pid: PaymentInputData): Observable<SwishLink> {
    const user = this.auth.firebaseUser;
    console.log(`getPayStatusHref() 1 ${user}`);
    if (!user) {
      return throwError(() => "Ingen användare");
    }
    console.log("paySwish()");
    // @ts-ignore
    const href = this.getPayStatusHref(user);
    console.log("Swish href", href);
    return this.api.swishLink(pid.tipParam, href, pid.receipt_key, pid.itemRefs, pid.plKey, pid.payableRefs, pid.chargeItems, undefined, undefined).pipe(
      map(sl => {
        const link = this.openSwishApp(sl);

        //Build pre navigate url to make sure we open same tab in mobile browsers
        const urlparts = decodeURIComponent(link).split("/paystatus/");
        if (urlparts.length === 2) {
          const navUrl = `${this.appState.venueId}/paystatus/${urlparts[1]}`;
          console.log("nav to: " + navUrl);
          sl.navUrl = navUrl;
        }
        return sl;
      })
    );
  }

  // Vipps MobilePay (WEB_REDIRECT): the server creates the payment and we send the browser to Vipps. On mobile the
  // Vipps app opens, on desktop a phone-number dialog; Vipps then returns the browser to the paystatus page, which
  // reads the outcome from Firestore (webhook/polling on the server), never from the redirect itself.
  private payVipps(pid: PaymentInputData): Observable<VippsLink> {
    const user = this.auth.firebaseUser;
    if (!user) {
      return throwError(() => "Ingen användare");
    }
    const href = this.getPayStatusHref(user, pid.plKey);
    return this.api.vippsLink(pid.tipParam, href, pid.receipt_key, pid.itemRefs, pid.plKey, pid.payableRefs, pid.chargeItems).pipe(
      map(vl => {
        console.log("redirect to Vipps: " + vl.redirect_url);
        window.location.href = vl.redirect_url;
        return vl;
      })
    );
  }

  private payPrepaid(pid: PaymentInputData): Observable<Order> {
    return this.orderService.payWithPrepaid(pid.tipParam, pid.receipt_key, pid.itemRefs, pid.plKey, pid.payableRefs);
  }

  private payAccount(pid: PaymentInputData): Observable<Order> {
    return this.orderService.payWithAccount(pid.tipParam, pid.receipt_key, pid.itemRefs, pid.plKey, pid.payableRefs);
  }

  private openBamboraCheckout(session: BamboraSession): Observable<any> {
    console.log(`Open bambora checkout: ${session}`);
    return new Observable(obs => {
      try {
        // @ts-ignore
        const checkout = new Bambora.RedirectCheckout(session.token);
        obs.next(session);
        obs.complete();
      } catch (error) {
        console.error('Bambora checkout error:', error);
        obs.error(error);
      }
    });
  }

  async pay(pid: PaymentInputData): Promise<PaymentOutputData> {
    try {
      switch (pid.method) {
        case "new_card":
          const newCardRes = await firstValueFrom(this.payBambora(pid));
          return { method: pid.method, methodResponse: newCardRes };
        case "saved_card":
          const savedCardRes = await firstValueFrom(this.pay_bambora_saved_card(pid));
          return { method: pid.method, methodResponse: savedCardRes, checkPayStatusReceiptKey: savedCardRes.receipt_key };
        case "card_gp":
          pid.subType = "google_pay";
          const cardGpRes = await firstValueFrom(this.payBambora(pid));
          return { method: pid.method, methodResponse: cardGpRes };
        case "prepaid":
          const prepOrder = await firstValueFrom(this.payPrepaid(pid));
          return { method: pid.method, methodResponse: prepOrder, };
        case "swish":
          const swishLink = await firstValueFrom(this.paySwish(pid));
          return { method: pid.method, methodResponse: swishLink, checkPayStatusReceiptKey: swishLink.receipt_key, navUrl: swishLink.navUrl };
        case "vipps":
          // The browser is leaving for Vipps; no in-app navigation, the return URL lands on paystatus
          const vippsLink = await firstValueFrom(this.payVipps(pid));
          return { method: pid.method, methodResponse: vippsLink };
        case "account":
          const res = await firstValueFrom(this.payAccount(pid));
          return { method: pid.method, methodResponse: res };
        case "free":
          const free = await firstValueFrom(this.orderService.payWithFree(pid.tipParam, pid.receipt_key, pid.itemRefs));
          // pid.receipt_key (livesplit identity format) cannot address the paystatus Firestore doc —
          // use the urlsafe key from the response. Cart flow (no receipt_key) keeps navigating to feed.
          return { method: pid.method, methodResponse: free, checkPayStatusReceiptKey: pid.receipt_key != null ? free.receipt_key : undefined };

        default:
          console.error(`Unknown payment method: ${pid.method}`);
          throw new Error("Okänd betalmetod");
      }
    } catch (error) {
      console.error('Payment error:', error);

      // Extract the error message properly from HTTP errors
      let errorMessage = 'Okänt fel';
      if ((error as any)?.error?.message) {
        errorMessage = (error as any).error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      // A payment link guest goes back to the link from the failure page, not to the (possibly empty) table bill
      const pl = pid.plKey ? `?pl=${encodeURIComponent(pid.plKey)}` : "";
      return {
        method: pid.method,
        methodResponse: { error: errorMessage },
        navUrl: `${this.appState.venueId}/${pid.declineRoute}${pl}`
      };
    }
  }

}
